import { API_ENDPOINTS } from '../constants';

import { apiClient, get, post, del, put } from './apiClient';

export interface TeacherInfo {
  id: string;
  full_name?: string;
  username?: string;
  email?: string;
}

export interface TemplateInfo {
  id: string;
  name?: string;
  description?: string;
}

export type EvaluationStatus =
  | 'draft'
  | 'pending'
  | 'in_progress'
  | 'scored'
  | 'completed'
  | 'approved'
  | 'archived';

export interface EvaluationSummary {
  id: string;
  teacher_id: number;
  teacher_name?: string;
  evaluator_id: number;
  evaluator_name?: string;
  template_id: string;
  rubric_template_id: string;
  evaluation_period: string;
  status: EvaluationStatus;
  total_score: number | null;
  max_score: number | null;
  percentage_score: number | null;
  performance_level: string | null;
  created_at: string;
  updated_at: string | null;
  scored_at: string | null;
  completion_percentage?: number;
  metadata: Record<string, unknown> | null;
  teacher: TeacherInfo | null;
  template: TemplateInfo | null;
}

export type EvaluationDetails = EvaluationSummary;

export interface EvaluationScore {
  domain_id: string;
  domain_name: string;
  score: number;
  max_score: number;
  evidence: string[];
  comments?: string;
}

export interface VideoUpload {
  id: number;
  filename: string;
  file_size: number;
  status: string;
  upload_progress: number;
  duration_seconds: number | null;
  resolution: string | null;
  quality_assessment: string | null;
  faces_detected: number;
  ferpa_compliant: boolean;
  error_message: string | null;
  created_at: string;
  uploaded_at: string | null;
  processed_at: string | null;
}

export interface VideoAnalytics {
  video_upload_id: number;
  total_speech_time: number | null;
  teacher_speech_time: number | null;
  student_speech_time: number | null;
  student_engagement_score: number | null;
  teacher_movement_score: number | null;
  question_count: number | null;
  response_count: number | null;
  overall_sentiment_score: number | null;
}

export interface CreateEvaluationRequest {
  teacher_id: number;
  rubric_template_id: string;
  evaluation_period?: string;
  metadata?: Record<string, unknown>;
  status?: EvaluationStatus;
}

export interface CreateEvaluationResponse {
  evaluation_id: string;
  message: string;
  status: string;
}

export interface EvaluationFilters {
  teacher_id?: string;
  status_filter?: string;
  evaluation_period?: string;
  search?: string; // Search by teacher name, template name, or evaluation period
  skip?: number;
  limit?: number;
}

export interface EvaluationUpdateRequest {
  teacher_id?: number;
  rubric_template_id?: string;
  evaluation_period?: string;
  evaluation_type?: string;
  observation_date?: string;
  evaluator_notes?: string;
  teacher_reflection?: string;
  evidence_summary?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  status?: EvaluationStatus;
}

export interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
  next_page: number | null;
  prev_page: number | null;
}

export interface EvaluationListResponse {
  data: EvaluationSummary[];
  pagination: PaginationMeta;
}

export interface EvaluationStats {
  total_evaluations: number;
  completed_evaluations: number;
  completion_rate: number;
  score_statistics: {
    count: number;
    average_score: number;
    min_score: number;
    max_score: number;
  };
  status_distribution: Record<string, number>;
  performance_level_distribution: Record<string, number>;
}

export interface VideoUploadInitRequest {
  filename: string;
  file_size: number;
  chunk_size?: number;
  teacher_id?: number;
  evaluation_purpose?: string;
}

export interface VideoUploadInitResponse {
  upload_id: number;
  upload_url: string;
  chunk_size: number;
  total_chunks: number;
  expires_at: string;
}

export interface ChunkUploadResponse {
  chunk_number: number;
  upload_progress: number;
  next_chunk_url: string | null;
  upload_complete: boolean;
}

export const getEvaluations = async (
  filters?: EvaluationFilters
): Promise<EvaluationListResponse> => {
  const params = new URLSearchParams();
  if (filters?.teacher_id) params.append('teacher_id', filters.teacher_id);
  if (filters?.status_filter)
    params.append('status_filter', filters.status_filter);
  if (filters?.evaluation_period)
    params.append('evaluation_period', filters.evaluation_period);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.skip !== undefined)
    params.append('skip', filters.skip.toString());
  if (filters?.limit !== undefined)
    params.append('limit', filters.limit.toString());

  const query = params.toString();
  return get<EvaluationListResponse>(
    query
      ? `${API_ENDPOINTS.EVALUATIONS.LIST}?${query}`
      : API_ENDPOINTS.EVALUATIONS.LIST
  );
};

// Alias for backward compatibility
export const listEvaluations = getEvaluations;

export const createEvaluation = (request: CreateEvaluationRequest) =>
  post<CreateEvaluationResponse>(API_ENDPOINTS.EVALUATIONS.CREATE, request);

export const getEvaluation = (evaluationId: string) =>
  get<EvaluationDetails>(API_ENDPOINTS.EVALUATIONS.GET(evaluationId));

export const deleteEvaluation = (evaluationId: string) =>
  del<{ message: string; evaluation_id: string }>(
    API_ENDPOINTS.EVALUATIONS.DELETE(evaluationId)
  );

export const processVideoForEvaluation = (
  evaluationId: string,
  videoId: number,
  lessonContext?: Record<string, unknown>
) =>
  post(API_ENDPOINTS.EVALUATIONS.PROCESS_VIDEO(evaluationId), {
    video_id: String(videoId),
    lesson_context: lessonContext,
  });

export const scoreEvaluation = (
  evaluationId: string,
  includeAiScoring = true,
  humanOverride?: Record<string, unknown>
) =>
  post<ScoreEvaluationResponse>(API_ENDPOINTS.EVALUATIONS.SCORE(evaluationId), {
    include_ai_scoring: includeAiScoring,
    human_override: humanOverride,
  });

export interface ScoreEvaluationResponse {
  message: string;
  evaluation_id: string;
  total_score: number;
  performance_level: string;
  domain_count: number;
  scored_components: number;
  status: string;
}

export interface RubricIndicatorDetail {
  id: string;
  component_id: string;
  performance_level: string;
  descriptor: string;
  keywords: string[] | null;
  score_range_min: number;
  score_range_max: number;
}

export interface RubricComponentDetail {
  id: string;
  domain_id: string;
  code: string;
  name: string;
  description: string | null;
  order_index: number;
  weight: number;
  indicators: RubricIndicatorDetail[];
}

export interface RubricDomainDetail {
  id: string;
  template_id: string;
  name: string;
  description: string | null;
  order_index: number;
  weight: number;
  components: RubricComponentDetail[];
}

export interface RubricTemplateFull {
  id: string;
  name: string;
  framework: string;
  description: string | null;
  domains: RubricDomainDetail[];
}

export interface EvaluationScoresResponse {
  evaluation_id: string;
  total_score: number | null;
  performance_level: string | null;
  status: string;
  domain_scores: Array<{
    domain_id: string;
    score: number;
    performance_level: string | null;
    evidence_summary: Record<string, unknown> | null;
  }>;
  component_scores: Array<{
    component_id: string;
    score: number;
    performance_level: string | null;
    notes: string | null;
  }>;
}

export const getFullRubricTemplate = (templateId: string) =>
  get<RubricTemplateFull>(API_ENDPOINTS.RUBRICS.TEMPLATE_FULL(templateId));

export const getEvaluationScores = (evaluationId: string) =>
  get<EvaluationScoresResponse>(API_ENDPOINTS.EVALUATIONS.SCORES(evaluationId));

export interface EvaluationReportPerson {
  id: number;
  name: string;
  email: string;
}

export interface EvaluationReportComponent {
  component_id: string;
  code: string | null;
  name: string | null;
  score: number;
  performance_level: string | null;
  rationale: string | null;
  evidence_used?: unknown[];
}

export interface EvaluationReportDomain {
  domain_id: string;
  name: string | null;
  score: number;
  performance_level: string | null;
  components: EvaluationReportComponent[];
  evidence_summary?: Record<string, unknown> | null;
}

export interface EvaluationReportRecommendation {
  domain_id: string;
  component_id: string;
  component_code: string;
  component_name: string | null;
  current_score: number;
  target_score: number;
  priority: 'high' | 'medium';
  focus_areas: string[];
  suggested_actions: string[];
}

export interface EvaluationReport {
  evaluation_id: string;
  teacher: EvaluationReportPerson | null;
  evaluator: EvaluationReportPerson | null;
  evaluation_period: string | null;
  evaluation_type: string | null;
  observation_date: string | null;
  status: string;
  rubric: { id: string; name: string; framework: string } | null;
  scores: {
    total_score: number | null;
    weighted_score: number | null;
    max_score: number | null;
    percentage: number | null;
    performance_level: string | null;
  };
  scored_at: string | null;
  evaluator_notes: string | null;
  teacher_reflection: string | null;
  domains: EvaluationReportDomain[];
  evidence?: unknown[];
  recommendations?: EvaluationReportRecommendation[];
}

export const getEvaluationReport = (evaluationId: string) =>
  get<EvaluationReport>(API_ENDPOINTS.EVALUATIONS.REPORT(evaluationId));

export const getEvaluationStats = async (
  teacherId?: string,
  period?: string
): Promise<EvaluationStats> => {
  try {
    const params = new URLSearchParams();
    if (teacherId) params.append('teacher_id', teacherId);
    if (period) params.append('period', period);

    const endpoint = params.toString()
      ? `${API_ENDPOINTS.EVALUATIONS.STATS}?${params.toString()}`
      : API_ENDPOINTS.EVALUATIONS.STATS;
    const response = await get<EvaluationStats>(endpoint);
    return response;
  } catch (error) {
    console.error('Get evaluation stats error:', error);
    throw error;
  }
};

export const updateEvaluation = (
  evaluationId: string,
  updateData: EvaluationUpdateRequest
) =>
  put<EvaluationDetails>(
    API_ENDPOINTS.EVALUATIONS.UPDATE(evaluationId),
    updateData
  );

export const initVideoUpload = (request: VideoUploadInitRequest) =>
  post<VideoUploadInitResponse>('/video/upload/init', request);

export const uploadVideoChunk = (
  uploadId: number,
  chunkNumber: number,
  chunk: Blob,
  chunkHash: string,
  isFinalChunk: boolean
) => {
  const formData = new FormData();
  formData.append('chunk_number', String(chunkNumber));
  formData.append('chunk_hash', chunkHash);
  formData.append('is_final_chunk', String(isFinalChunk));
  formData.append('chunk_file', chunk);

  return apiClient<ChunkUploadResponse>(`/video/upload/${uploadId}/chunk`, {
    method: 'POST',
    body: formData,
  });
};

export const getVideoUpload = (uploadId: number) =>
  get<VideoUpload>(`/video/uploads/${uploadId}`);

export const listVideoUploads = (teacherId?: number) => {
  const params = teacherId ? `?teacher_id=${teacherId}` : '';
  return get<VideoUpload[]>(`/video/uploads${params}`);
};

export const getVideoAnalytics = (uploadId: number) =>
  get<VideoAnalytics>(`/video/uploads/${uploadId}/analytics`);

export const triggerVideoProcessing = (uploadId: number) =>
  post<{ message: string; upload_id: number }>(
    `/video/uploads/${uploadId}/process`,
    {}
  );

// Export service object with all methods
export const evaluationService = {
  getEvaluations,
  listEvaluations, // Alias for backward compatibility
  createEvaluation,
  getEvaluation,
  updateEvaluation,
  deleteEvaluation,
  processVideoForEvaluation,
  scoreEvaluation,
  getFullRubricTemplate,
  getEvaluationScores,
  getEvaluationReport,
  getEvaluationStats,
  initVideoUpload,
  uploadVideoChunk,
  getVideoUpload,
  listVideoUploads,
  getVideoAnalytics,
  triggerVideoProcessing,
};
